import type { PortalIdentity } from '../../../model/portalModels';
import type {
  PortalMyTaskCounts,
  PortalWorkItemListItem,
} from '../../my-tasks/model/myTasks';
import { formatShanghai } from '../../my-tasks/model/myTasks';
import type {
  PortalNotificationPreview,
  PortalOperationList,
  PortalQuickTask,
} from '../../operations/types/portal.types';
import type { PortalPage } from '../../pages/api/pagesApi';

export type WorkbenchMetricKey =
  'todo' | 'urge' | 'cc-unread' | 'notification';

export type PortalWorkbenchMetric = {
  key: WorkbenchMetricKey;
  label: string;
  value: string;
  unit: string;
  note: string;
};

export type PortalWorkbenchData = {
  profile: {
    displayName: string;
    avatarUrl?: string;
    organizationName: string;
    roleName: string;
  };
  metrics: PortalWorkbenchMetric[];
  operationLists: PortalOperationList[];
  favoriteOperationIds: string[];
  favoritePages: PortalPage[];
  pageFavoriteError?: string;
  quickTasks: PortalQuickTask[];
  notificationPreviews: PortalNotificationPreview[];
};

export function buildWorkbenchMetrics(
  counts: PortalMyTaskCounts,
): PortalWorkbenchMetric[] {
  return [
    {
      key: 'todo', label: '待办', value: String(counts.todo), unit: '项',
      note: counts.todo > 0 ? '进入我的任务处理' : '当前没有待处理任务',
    },
    {
      key: 'urge', label: '催办', value: String(counts.urgent), unit: '项',
      note: counts.urgent > 0 ? '请优先处理' : '当前没有催办任务',
    },
    {
      key: 'cc-unread', label: '抄送未读', value: String(counts.cc), unit: '项',
      note: counts.cc > 0 ? '进入抄送列表查看' : '当前没有未读抄送',
    },
    {
      key: 'notification', label: '通知', value: '—', unit: '',
      note: '数据源待接入',
    },
  ];
}

export function buildQuickTasks(
  items: PortalWorkItemListItem[],
): PortalQuickTask[] {
  return items.filter(item => item.quickApprovable).slice(0, 3).map(item => ({
    taskId: item.taskId,
    targetTaskId: item.taskId,
    actionLabel: item.quickApprovalLabel?.trim() || '通过',
    title: item.title,
    sourceSystem: item.sourceSystem,
    initiator: item.initiator,
    summary: item.summary,
    summaryItems: item.summaryItems.slice(0, 3),
    receivedAt: formatShanghai(item.receivedAt),
    runtimeTaskVersion: item.runtimeTaskVersion,
  }));
}

export function buildWorkbenchProfile(identity: PortalIdentity) {
  const displayName = identity.actorName.trim() || '未设置';
  return {
    displayName,
    avatarUrl: identity.avatarUrl?.trim() || undefined,
    organizationName: identity.tenantName,
    roleName: identity.platformOperator ? '平台运营人员' : '门户用户',
  };
}

export function shanghaiWorkbenchTime(now = new Date()) {
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
    hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(part => part.type === type)?.value ?? '';
  const hour = Number(value('hour'));
  const greeting = hour < 6 ? '夜深了'
    : hour < 12 ? '上午好'
      : hour < 18 ? '下午好' : '晚上好';
  return {
    greeting,
    dateText: `${value('year')} 年 ${value('month')} ${value('day')} 日 · ${value('weekday')}`,
  };
}
